import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { EmailVerification, PinpointPartnerMessage } from '@feathr/blackbox';
import { CampaignState } from '@feathr/blackbox';
import { Alert, Button, Form } from '@feathr/components';
import type { TValidateGrouped } from '@feathr/rachis';

import ButtonNext from './ButtonNext';
import { validateStepFive } from './EventPartnersMessageStepFive';
import { validateStepFour } from './EventPartnersMessageStepFour';
import { validateStepOne } from './EventPartnersMessageStepOne';
import { validateStepSix } from './EventPartnersMessageStepSix';
import { validateStepTwo } from './EventPartnersMessageStepTwo';
import PartnerMessageSummary from './PartnerMessageSummary';

interface IProps {
  disabled?: boolean;
  emailVerification?: EmailVerification;
  message: PinpointPartnerMessage;
  onCancel: () => void;
  onSave: () => void;
  onPrevious: () => void;
}

export interface IEventPartnersMessageErrors extends TValidateGrouped {
  name?: string[];
  campaign?: string[];
  participation?: string[];
  from_address?: string[];
  from_name?: string[];
  status?: string[];
  subject?: string[];
  template?: string[];
  date_send_start?: string[];
}

export function validate(
  message: PinpointPartnerMessage,
  emailVerification?: EmailVerification,
): IEventPartnersMessageErrors {
  return {
    ...validateStepOne(message),
    ...validateStepTwo(message, emailVerification),
    ...validateStepFive(message),
    ...validateStepFour(message),
    ...validateStepSix(message),
  };
}

function EventPartnersMessageStepSeven({
  disabled = false,
  emailVerification,
  message,
  onCancel,
  onSave,
  onPrevious,
}: IProps): JSX.Element {
  const { t } = useTranslation();
  const state: CampaignState = message.get('state', CampaignState.Draft);

  function validateForButton(msg: PinpointPartnerMessage): IEventPartnersMessageErrors {
    return validate(msg, emailVerification);
  }

  const actions: ReactNode[] = [];
  if (state === 'draft') {
    actions.push(
      <Button key={'previous'} name={'previous_step'} onClick={onPrevious}>
        {t('Previous')}
      </Button>,
    );
    actions.push(
      <ButtonNext
        key={'send'}
        message={message}
        name={'send_message'}
        onNext={onSave}
        type={'success'}
        validate={validateForButton}
      >
        {t('Send')}
      </ButtonNext>,
    );
  } else if (state === CampaignState.Published && !message.isPastStartDate) {
    actions.push(
      <Button key={'previous'} name={'cancel_send'} onClick={onCancel} type={'danger'}>
        {t('Cancel sending')}
      </Button>,
    );
  }

  return (
    <Form
      actions={actions}
      description={t(
        "Check the summary below to make sure everything is configured correctly. When you're ready, click Send to finalize your message.",
      )}
      label={t('Edit Message: Review')}
    >
      {disabled && (
        <Alert
          description={t('Cancel sending first, make your changes, and re-publish.')}
          title={t('Changes require re-publishing')}
          type={'info'}
        />
      )}
      <PartnerMessageSummary
        message={message}
        validationErrors={validate(message, emailVerification)}
      />
    </Form>
  );
}

export default observer(EventPartnersMessageStepSeven);
